import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { itemService } from "../../api/services/itemService";
import { isUserLoggedIn } from "../../utils/authUtils";
import Loader from "../Loader/Loader";
import "./CreateAccount.css";

/** Must be a reCAPTCHA v3 site key (v2 checkbox keys will not work with this integration). */
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

/** DevTools: filter console by `[reCAPTCHA v3]`. Set REACT_APP_DEBUG_RECAPTCHA=false to silence in development. */
const RECAPTCHA_DEBUG =
  process.env.REACT_APP_DEBUG_RECAPTCHA !== "false" &&
  (process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_DEBUG_RECAPTCHA === "true");

const PAUSE_ON_RECAPTCHA =
  process.env.REACT_APP_DEBUG_RECAPTCHA_PAUSE === "true";

function logRecaptcha(...args) {
  if (RECAPTCHA_DEBUG) {
    console.log("[reCAPTCHA v3]", ...args);
  }
}

const CreateAccountForm = ({ getRecaptchaToken }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    lastName: "",
    country: "United States",
    userName: "",
    email: "",
    mobileNumber: "",
    countryCode: "+1",
    password: "",
    confirmPassword: "",
    textNotifications: false,
    legalEntityName: "",
    businessName: "",
    streetAddress: "",
    suiteUnitFloor: "",
    city: "",
    state: "",
    zipCode: "",
    businessCountry: "United States",
    storeNumber: "",
    storeCountryCode: "+1",
    companyWebsite: "",
    taxIdType: "EIN",
    taxIdNumber: "",
    preferredLanguage: "English (English)",
    preferredCurrency: "US Dollar",
    howDidYouHear: "",
    businessDescription: "",
    newsletterUpdates: false,
    termsAndConditions: false,
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [userNameError, setUserNameError] = useState("");
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const otpInputRefs = useRef([]);

  // If already logged in, redirect away from create account page
  useEffect(() => {
    if (isUserLoggedIn()) {
      navigate("/my-account", { replace: true });
    }
  }, [navigate]);

  // Show OTP modal when page loads
  useEffect(() => {
    // Focus first OTP input after modal opens
    setTimeout(() => {
      otpInputRefs.current[0]?.focus();
    }, 100);
  }, []);

  const countries = [
    "United States",
    "Canada",
    "United Kingdom",
    "Australia",
    "Germany",
    "France",
    "Italy",
    "Spain",
    "Netherlands",
    "Belgium",
    "Switzerland",
    "Austria",
    "Sweden",
    "Norway",
    "Denmark",
    "Finland",
    "Japan",
    "South Korea",
    "China",
    "India",
    "Brazil",
    "Mexico",
    "Argentina",
    "Chile",
    "New Zealand",
    "Ireland",
    "Portugal",
    "Poland",
    "Czech Republic",
    "Hungary",
    "Romania",
    "Bulgaria",
    "Croatia",
    "Slovenia",
    "Slovakia",
    "Estonia",
    "Latvia",
    "Lithuania",
    "Greece",
    "Cyprus",
    "Malta",
    "Luxembourg",
    "Iceland",
    "Liechtenstein",
    "Monaco",
    "San Marino",
    "Vatican City",
    "Andorra",
  ];

  const countryCodes = [
    {
      code: "+1",
      country: "US",
      flag: "https://flagcdn.com/w20/us.png",
      display: "US +1",
    },
    {
      code: "+1",
      country: "CA",
      flag: "https://flagcdn.com/w20/ca.png",
      display: "CA +1",
    },
    {
      code: "+44",
      country: "UK",
      flag: "https://flagcdn.com/w20/gb.png",
      display: "UK +44",
    },
    {
      code: "+61",
      country: "AU",
      flag: "https://flagcdn.com/w20/au.png",
      display: "AU +61",
    },
    {
      code: "+49",
      country: "DE",
      flag: "https://flagcdn.com/w20/de.png",
      display: "DE +49",
    },
    {
      code: "+33",
      country: "FR",
      flag: "https://flagcdn.com/w20/fr.png",
      display: "FR +33",
    },
    {
      code: "+39",
      country: "IT",
      flag: "https://flagcdn.com/w20/it.png",
      display: "IT +39",
    },
    {
      code: "+34",
      country: "ES",
      flag: "https://flagcdn.com/w20/es.png",
      display: "ES +34",
    },
    {
      code: "+31",
      country: "NL",
      flag: "https://flagcdn.com/w20/nl.png",
      display: "NL +31",
    },
    {
      code: "+32",
      country: "BE",
      flag: "https://flagcdn.com/w20/be.png",
      display: "BE +32",
    },
    {
      code: "+41",
      country: "CH",
      flag: "https://flagcdn.com/w20/ch.png",
      display: "CH +41",
    },
    {
      code: "+43",
      country: "AT",
      flag: "https://flagcdn.com/w20/at.png",
      display: "AT +43",
    },
    {
      code: "+46",
      country: "SE",
      flag: "https://flagcdn.com/w20/se.png",
      display: "SE +46",
    },
    {
      code: "+47",
      country: "NO",
      flag: "https://flagcdn.com/w20/no.png",
      display: "NO +47",
    },
    {
      code: "+45",
      country: "DK",
      flag: "https://flagcdn.com/w20/dk.png",
      display: "DK +45",
    },
    {
      code: "+358",
      country: "FI",
      flag: "https://flagcdn.com/w20/fi.png",
      display: "FI +358",
    },
    {
      code: "+81",
      country: "JP",
      flag: "https://flagcdn.com/w20/jp.png",
      display: "JP +81",
    },
    {
      code: "+82",
      country: "KR",
      flag: "https://flagcdn.com/w20/kr.png",
      display: "KR +82",
    },
    {
      code: "+86",
      country: "CN",
      flag: "https://flagcdn.com/w20/cn.png",
      display: "CN +86",
    },
    {
      code: "+91",
      country: "IN",
      flag: "https://flagcdn.com/w20/in.png",
      display: "IN +91",
    },
    {
      code: "+55",
      country: "BR",
      flag: "https://flagcdn.com/w20/br.png",
      display: "BR +55",
    },
    {
      code: "+52",
      country: "MX",
      flag: "https://flagcdn.com/w20/mx.png",
      display: "MX +52",
    },
    {
      code: "+54",
      country: "AR",
      flag: "https://flagcdn.com/w20/ar.png",
      display: "AR +54",
    },
    {
      code: "+56",
      country: "CL",
      flag: "https://flagcdn.com/w20/cl.png",
      display: "CL +56",
    },
    {
      code: "+64",
      country: "NZ",
      flag: "https://flagcdn.com/w20/nz.png",
      display: "NZ +64",
    },
    {
      code: "+353",
      country: "IE",
      flag: "https://flagcdn.com/w20/ie.png",
      display: "IE +353",
    },
    {
      code: "+351",
      country: "PT",
      flag: "https://flagcdn.com/w20/pt.png",
      display: "PT +351",
    },
    {
      code: "+48",
      country: "PL",
      flag: "https://flagcdn.com/w20/pl.png",
      display: "PL +48",
    },
    {
      code: "+420",
      country: "CZ",
      flag: "https://flagcdn.com/w20/cz.png",
      display: "CZ +420",
    },
    {
      code: "+36",
      country: "HU",
      flag: "https://flagcdn.com/w20/hu.png",
      display: "HU +36",
    },
    {
      code: "+40",
      country: "RO",
      flag: "https://flagcdn.com/w20/ro.png",
      display: "RO +40",
    },
    {
      code: "+359",
      country: "BG",
      flag: "https://flagcdn.com/w20/bg.png",
      display: "BG +359",
    },
    {
      code: "+385",
      country: "HR",
      flag: "https://flagcdn.com/w20/hr.png",
      display: "HR +385",
    },
    {
      code: "+386",
      country: "SI",
      flag: "https://flagcdn.com/w20/si.png",
      display: "SI +386",
    },
    {
      code: "+421",
      country: "SK",
      flag: "https://flagcdn.com/w20/sk.png",
      display: "SK +421",
    },
    {
      code: "+372",
      country: "EE",
      flag: "https://flagcdn.com/w20/ee.png",
      display: "EE +372",
    },
    {
      code: "+371",
      country: "LV",
      flag: "https://flagcdn.com/w20/lv.png",
      display: "LV +371",
    },
    {
      code: "+370",
      country: "LT",
      flag: "https://flagcdn.com/w20/lt.png",
      display: "LT +370",
    },
    {
      code: "+30",
      country: "GR",
      flag: "https://flagcdn.com/w20/gr.png",
      display: "GR +30",
    },
    {
      code: "+357",
      country: "CY",
      flag: "https://flagcdn.com/w20/cy.png",
      display: "CY +357",
    },
    {
      code: "+356",
      country: "MT",
      flag: "https://flagcdn.com/w20/mt.png",
      display: "MT +356",
    },
    {
      code: "+352",
      country: "LU",
      flag: "https://flagcdn.com/w20/lu.png",
      display: "LU +352",
    },
    {
      code: "+354",
      country: "IS",
      flag: "https://flagcdn.com/w20/is.png",
      display: "IS +354",
    },
    {
      code: "+423",
      country: "LI",
      flag: "https://flagcdn.com/w20/li.png",
      display: "LI +423",
    },
    {
      code: "+377",
      country: "MC",
      flag: "https://flagcdn.com/w20/mc.png",
      display: "MC +377",
    },
    {
      code: "+378",
      country: "SM",
      flag: "https://flagcdn.com/w20/sm.png",
      display: "SM +378",
    },
    {
      code: "+39",
      country: "VA",
      flag: "https://flagcdn.com/w20/va.png",
      display: "VA +39",
    },
    {
      code: "+376",
      country: "AD",
      flag: "https://flagcdn.com/w20/ad.png",
      display: "AD +376",
    },
  ];

  const usStates = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ];

  const taxIdTypes = ["EIN", "SSN", "ITIN", "Other"];

  const languages = [
    "English (English)",
    "Spanish (Español)",
    "French (Français)",
    "German (Deutsch)",
    "Italian (Italiano)",
    "Portuguese (Português)",
    "Chinese (中文)",
    "Japanese (日本語)",
    "Korean (한국어)",
    "Arabic (العربية)",
  ];

  const currencies = [
    "US Dollar",
    "Euro",
    "British Pound",
    "Canadian Dollar",
    "Australian Dollar",
    "Japanese Yen",
    "Chinese Yuan",
    "Swiss Franc",
    "Swedish Krona",
    "Norwegian Krone",
  ];

  const howDidYouHearOptions = [
    "Google Search",
    "Social Media",
    "Referral from friend/colleague",
    "Trade show/Conference",
    "Online advertisement",
    "Print advertisement",
    "Email marketing",
    "Partner recommendation",
    "Other",
  ];

  const businessDescriptionOptions = [
    "Electronics Retailer",
    "Mobile Phone Repair Shop",
    "Computer Repair Service",
    "Electronics Wholesaler",
    "Online Electronics Store",
    "Mobile Accessories Store",
    "IT Services Provider",
    "Electronics Distributor",
    "Repair Parts Supplier",
    "Other",
  ];

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle username email validation
    if (name === "userName") {
      if (value && !validateEmail(value)) {
        setUserNameError("Please enter a valid email address");
      } else {
        setUserNameError("");
      }
    }

    // Handle password match validation
    if (name === "password" || name === "confirmPassword") {
      const passwordValue = name === "password" ? value : formData.password;
      const confirmPasswordValue =
        name === "confirmPassword" ? value : formData.confirmPassword;

      if (
        confirmPasswordValue &&
        passwordValue &&
        passwordValue !== confirmPasswordValue
      ) {
        setPasswordMatchError("Passwords do not match");
      } else {
        setPasswordMatchError("");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Helper function to extract language code (e.g., "En" from "English (English)")
  const getLanguageCode = (language) => {
    const languageMap = {
      "English (English)": "En",
      "Spanish (Español)": "Es",
      "French (Français)": "Fr",
      "German (Deutsch)": "De",
      "Italian (Italiano)": "It",
      "Portuguese (Português)": "Pt",
      "Chinese (中文)": "Zh",
      "Japanese (日本語)": "Ja",
      "Korean (한국어)": "Ko",
      "Arabic (العربية)": "Ar",
    };
    return languageMap[language] || "En";
  };

  // Helper function to extract currency code (e.g., "USA" from "US Dollar")
  const getCurrencyCode = (currency) => {
    const currencyMap = {
      "US Dollar": "USA",
      Euro: "EUR",
      "British Pound": "GBP",
      "Canadian Dollar": "CAD",
      "Australian Dollar": "AUD",
      "Japanese Yen": "JPY",
      "Chinese Yuan": "CNY",
      "Swiss Franc": "CHF",
      "Swedish Krona": "SEK",
      "Norwegian Krone": "NOK",
    };
    return currencyMap[currency] || "USA";
  };

  // Handle OTP input change
  const handleOTPChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value;
    setOtpDigits(newOtpDigits);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP backspace
  const handleOTPKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtpDigits = pastedData
        .split("")
        .concat(Array(6 - pastedData.length).fill(""));
      setOtpDigits(newOtpDigits.slice(0, 6));
      // Focus the last filled input or the last input
      const focusIndex = Math.min(pastedData.length - 1, 5);
      setTimeout(() => otpInputRefs.current[focusIndex]?.focus(), 0);
    }
  };

  // Handle OTP Continue - Verify OTP first, then create account
  const handleOTPContinue = async () => {
    const otpCode = otpDigits.join("");
    if (otpCode.length !== 6) {
      setOtpError("Please enter the complete 6-digit code.");
      return;
    }

    setOtpVerifying(true);
    setOtpError("");

    try {
      // Verify OTP with API
      const verifyResponse = await itemService.verifyOTP(
        formData.email,
        otpCode,
      );

      // Check if OTP verification was successful
      if (
        verifyResponse &&
        (verifyResponse.success ||
          verifyResponse.Status === "Success" ||
          verifyResponse.IsValid === true ||
          verifyResponse.isValid === true)
      ) {
        // OTP verified successfully, now proceed with account creation
        setShowOTPModal(false);
        await proceedWithAccountCreation();
      } else {
        // OTP verification failed
        setOtpError(
          verifyResponse?.message ||
            verifyResponse?.Message ||
            "Invalid OTP code. Please try again.",
        );
        // Reset OTP inputs
        setOtpDigits(["", "", "", "", "", ""]);
        setTimeout(() => {
          otpInputRefs.current[0]?.focus();
        }, 0);
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to verify OTP. Please try again.",
      );
      // Reset OTP inputs on error
      setOtpDigits(["", "", "", "", "", ""]);
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 0);
    } finally {
      setOtpVerifying(false);
    }
  };

  // Handle Resend Code
  const handleResendCode = async () => {
    setOtpError("");
    setOtpVerifying(true);

    try {
      // Call API to resend OTP
      const resendResponse = await itemService.resendOTP(formData.email);

      if (
        resendResponse &&
        (resendResponse.success ||
          resendResponse.Status === "Success" ||
          resendResponse.Message)
      ) {
        // Reset OTP inputs
        setOtpDigits(["", "", "", "", "", ""]);
        // Show success message (optional)
        setSuccessMessage("OTP code has been resent to your email.");
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
        // Focus first input
        setTimeout(() => {
          otpInputRefs.current[0]?.focus();
        }, 0);
      } else {
        setOtpError(
          resendResponse?.message ||
            resendResponse?.Message ||
            "Failed to resend OTP. Please try again.",
        );
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setOtpError(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  // Proceed with account creation after OTP verification
  const proceedWithAccountCreation = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Prepare API payload
      const accountData = {
        CustomerInfo: {
          FirstName: formData.fullName,
          LastName: formData.lastName,
          Country: formData.country,
          UserName: formData.userName || formData.email,
          // Email: formData.email || formData.userName, // commented per request - do not send Email on create account API
          PhoneNumber: `${String(formData.countryCode || "").replace("+", "")}${String(formData.mobileNumber || "").replace(/-/g, "")}`,
          Password: formData.password,
          IsReceiveTextNotifications: formData.textNotifications,
        },
        BusinessInfo: {
          LegalEntityName: formData.legalEntityName,
          BusinessName: formData.businessName,
          BusinessCategory: 0, // Default to 0 as per API example
          BusinessStreetAddress: formData.streetAddress,
          BusinessSuite: formData.suiteUnitFloor || "",
          BusinessCity: formData.city,
          BusinessZipCode: formData.zipCode,
          BusinessState: formData.state,
          BusinessCountry: formData.businessCountry,
          PhoneNumber: `${String(formData.storeCountryCode || "").replace("+", "")}${String(formData.storeNumber || "").replace(/-/g, "")}`,
          BusinessWesbite: formData.companyWebsite || "",
          EIN: formData.taxIdNumber,
        },
        AdditionalInfo: {
          PreferrredLanguage: getLanguageCode(formData.preferredLanguage),
          PreferrredCurrency: getCurrencyCode(formData.preferredCurrency),
          HowDidYouHearAboutUs: formData.howDidYouHear || "",
          IsReceivePromotionalEmails: formData.newsletterUpdates,
          IsAgreeTermsAndConditions: formData.termsAndConditions,
        },
      };

      const response = await itemService.accountCreation(accountData);

      // Check if account creation was successful (success === 1 or truthy success flag)
      const isSuccess =
        response &&
        (response.success === 1 ||
          response.success === true ||
          response.Status === "Success" ||
          response.UserId ||
          response.userId);
      if (isSuccess) {
        setSuccessMessage(
          "Account created successfully! Redirecting to login...",
        );
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        const msg =
          response?.error_msg ??
          response?.message ??
          "Failed to create account. Please try again.";
        setErrorMessage(String(msg).trim().replace(/\n/g, " "));
      }
    } catch (error) {
      console.error("Account creation error:", error);
      const data = error.response?.data;
      const msg = data?.error_msg ?? data?.message ?? data?.error;
      setErrorMessage(
        msg
          ? String(msg).trim().replace(/\n/g, " ")
          : "Failed to create account. Please check your information and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Require site protection to be configured before account creation
    if (!RECAPTCHA_SITE_KEY) {
      setErrorMessage(
        "Account creation is temporarily unavailable because site protection is not configured.",
      );
      return;
    }

    // Validate terms and conditions
    if (!formData.termsAndConditions) {
      setErrorMessage("Please agree to the Terms and Conditions to continue.");
      return;
    }

    // Required: Legal Entity Name
    if (!formData.legalEntityName?.trim()) {
      setErrorMessage("Please enter Legal Entity Name (e.g. Acme LLC).");
      return;
    }

    // Required: Business Name
    if (!formData.businessName?.trim()) {
      setErrorMessage("Please enter Business Name (DBA/Trade name).");
      return;
    }

    // Validate username as email (Email Address field is hidden; username is the email)
    if (!formData.userName?.trim()) {
      setUserNameError("Please enter a valid email address");
      setErrorMessage(
        "Please enter your email address in the User Name field.",
      );
      return;
    }
    if (!validateEmail(formData.userName.trim())) {
      setUserNameError("Please enter a valid email address");
      setErrorMessage(
        "Please enter a valid email address in the User Name field.",
      );
      return;
    }
    setUserNameError("");

    if (RECAPTCHA_SITE_KEY && getRecaptchaToken) {
      logRecaptcha("Submit: running executeRecaptcha(\"create_account\") …");
      if (PAUSE_ON_RECAPTCHA) {
        // eslint-disable-next-line no-debugger -- optional breakpoint when REACT_APP_DEBUG_RECAPTCHA_PAUSE=true
        debugger;
      }
      try {
        const token = await getRecaptchaToken();
        if (!token) {
          logRecaptcha("Submit: no token returned (empty or null)");
          setErrorMessage(
            "Verification could not be completed. Please try again.",
          );
          return;
        }
        logRecaptcha("Submit: token received", {
          length: token.length,
          preview: `${token.slice(0, 28)}…`,
        });
      } catch (err) {
        logRecaptcha("Submit: executeRecaptcha threw", err);
        console.error("reCAPTCHA error:", err);
        setErrorMessage(
          "Verification could not be completed. Please try again.",
        );
        return;
      }
    } else {
      logRecaptcha("Submit: skipped (no site key or no getRecaptchaToken)");
    }

    // OTP modal hidden for now – proceed directly to account creation
    await proceedWithAccountCreation();
  };

  const closeErrorPopup = () => {
    setErrorMessage("");
  };

  const closeSuccessPopup = () => {
    setSuccessMessage("");
  };

  return (
    <div className="create-account-page">
      {/* Loader Overlay */}
      {loading && <Loader />}

      {/* Error Popup */}
      {errorMessage && (
        <div className="error-popup" onClick={closeErrorPopup}>
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="error-close"
              onClick={closeErrorPopup}
              aria-label="Close"
            >
              ×
            </button>
            <p className="error-message">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {successMessage && (
        <div className="error-popup success-popup" onClick={closeSuccessPopup}>
          <div
            className="error-popup-content success-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="error-close"
              onClick={closeSuccessPopup}
              aria-label="Close"
            >
              ×
            </button>
            <p className="error-message success-message">{successMessage}</p>
          </div>
        </div>
      )}
      {/* Create Your Account Section */}
      <div className="create-account-header">
        <div className="create-account-header-content">
          <div className="create-account-title-section">
            <h1 className="create-account-title">Create Your Account</h1>
            <div className="create-account-warehouse-selector">
              <span className="create-account-warehouse-text">
                US Warehouse
              </span>
              <span className="create-account-dropdown-arrow">▼</span>
            </div>
          </div>

          <p className="create-account-description">
            Join thousands of repair professionals accessing wireox complete
            repair parts and solutions from our US facility. US's largest and
            trusted wholesale distributor with lifetime warranties and same-day
            shipping coast to coast. Get everything from devices to tools with
            real-time US support and hassle-free returns.
          </p>

          <p className="create-account-sign-in-prompt">
            Already have an account?{" "}
            <span
              className="create-account-sign-in-link"
              onClick={() => navigate("/login")}
            >
              Sign in
            </span>
          </p>
        </div>
      </div>

      {/* Separator Line */}
      <div className="create-account-section-separator"></div>

      {/* Customer Information Section */}
      <div className="create-account-customer-information-section">
        <h2 className="create-account-customer-info-title">
          Customer Information
        </h2>

        <form
          onSubmit={handleSubmit}
          className="create-account-customer-info-form"
        >
          <div className="create-account-form-row">
            <div className="create-account-form-field">
              <label htmlFor="fullName" className="create-account-field-label">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                placeholder="Your First Name"
                value={formData.fullName}
                onChange={handleInputChange}
                className="create-account-customer-input"
                required
              />
            </div>

            <div className="create-account-form-field">
              <label htmlFor="lastName" className="create-account-field-label">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                className="create-account-customer-input"
                required
              />
            </div>
          </div>

          <div className="create-account-form-field">
            <label htmlFor="country" className="create-account-field-label">
              Country
            </label>
            <div className="create-account-country-selector">
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="create-account-customer-input create-account-country-select"
                required
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="create-account-form-field">
            <label htmlFor="userName" className="create-account-field-label">
              User Name
            </label>
            <input
              type="email"
              id="userName"
              name="userName"
              placeholder="Enter Email Address"
              value={formData.userName}
              onChange={handleInputChange}
              className={`create-account-customer-input ${
                userNameError ? "error-input" : ""
              }`}
              required
            />
            {userNameError && (
              <p className="create-account-error-text">{userNameError}</p>
            )}
          </div>

          <div
            className="create-account-form-field create-account-email-field-hidden"
            style={{ display: "none" }}
          >
            <label htmlFor="email" className="create-account-field-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter Email Address"
              value={formData.email || formData.userName}
              onChange={handleInputChange}
              className="create-account-customer-input"
              aria-hidden="true"
            />
          </div>

          <div className="create-account-form-field">
            <label
              htmlFor="mobileNumber"
              className="create-account-field-label"
            >
              Mobile Number
            </label>
            <p className="create-account-mobile-description">
              Make sure you can access this number, we'll text a verification
              code
            </p>
            <div className="create-account-mobile-container">
              <div className="create-account-country-code-selector">
                <div className="create-account-country-code-display">
                  <img
                    src={
                      countryCodes.find(
                        (item) => item.code === formData.countryCode,
                      )?.flag || countryCodes[0].flag
                    }
                    alt="flag"
                    className="create-account-flag-image"
                  />
                  <span className="create-account-country-code-text">
                    {formData.countryCode}
                  </span>
                  <span className="create-account-dropdown-arrow">▼</span>
                </div>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  className="create-account-country-code-select"
                >
                  {countryCodes.map((item) => (
                    <option
                      key={`${item.code}-${item.country}`}
                      value={item.code}
                    >
                      {item.display}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                placeholder="123-456-789"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                className="create-account-mobile-input"
                required
              />
            </div>
          </div>

          <div className="create-account-disclaimer">
            <p className="create-account-disclaimer-text">
              <strong>Disclaimer:</strong> By providing your mobile number, you
              consent to receive transactional text messages including one-time
              passcodes (OTP) for account security. Message and data rates may
              apply. Reply STOP to opt out.
            </p>
          </div>

          <div className="create-account-checkbox-container">
            <input
              type="checkbox"
              id="textNotifications"
              name="textNotifications"
              checked={formData.textNotifications}
              onChange={handleInputChange}
              className="create-account-checkbox"
            />
            <label
              htmlFor="textNotifications"
              className="create-account-checkbox-label"
            >
              I'd like to receive text notifications about new products,
              promotions, and price drops
            </label>
          </div>

          <div className="create-account-form-field">
            <label htmlFor="password" className="create-account-field-label">
              Password
            </label>
            <div className="create-account-password-container">
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Enter Password"
                value={formData.password}
                onChange={handleInputChange}
                className="create-account-password-input"
                required
              />
              <button
                type="button"
                className="create-account-password-toggle"
                onClick={() => {
                  const passwordInput = document.getElementById("password");
                  passwordInput.type =
                    passwordInput.type === "password" ? "text" : "password";
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
          </div>

          <div className="create-account-password-requirements">
            <ul className="create-account-requirements-list">
              <li>Between 7-16 characters long</li>
              <li>Mix of uppercase and lowercase letters</li>
              <li>Contain at least 1 number</li>
              <li>Contain at least 1 special character</li>
            </ul>
          </div>

          <div className="create-account-form-field">
            <label
              htmlFor="confirmPassword"
              className="create-account-field-label"
            >
              Confirm Password
            </label>
            <div className="create-account-password-container">
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`create-account-password-input ${
                  passwordMatchError ? "error-input" : ""
                }`}
                required
              />
              <button
                type="button"
                className="create-account-password-toggle"
                onClick={() => {
                  const confirmPasswordInput =
                    document.getElementById("confirmPassword");
                  confirmPasswordInput.type =
                    confirmPasswordInput.type === "password"
                      ? "text"
                      : "password";
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
            {passwordMatchError && (
              <p className="create-account-error-text">{passwordMatchError}</p>
            )}
          </div>
        </form>
      </div>

      {/* Business Information Section */}
      <div className="create-account-business-information-section">
        <h2 className="create-account-business-info-title">
          Business Information
        </h2>

        <p className="create-account-business-description">
          Provide your business information. The entered address will also be
          set as your default shipping and billing methods. This can be changed
          later in your account settings.
        </p>

        <form
          onSubmit={handleSubmit}
          className="create-account-business-form"
          noValidate
        >
          <div className="create-account-form-field">
            <label
              htmlFor="legalEntityName"
              className="create-account-field-label"
            >
              Legal Entity Name{" "}
              <span className="create-account-required">*</span>
            </label>
            <p className="create-account-entity-hint">
              Entity name including LLC, Inc, etc. (Example: Acme LLC)
            </p>
            <input
              type="text"
              id="legalEntityName"
              name="legalEntityName"
              placeholder="Enter an entity name"
              value={formData.legalEntityName}
              onChange={handleInputChange}
              className="create-account-customer-input"
              required
            />
          </div>

          <div className="create-account-form-field">
            <label
              htmlFor="businessName"
              className="create-account-field-label"
            >
              Business Name <span className="create-account-required">*</span>
            </label>
            <p className="create-account-business-hint">
              Business name as shown publicly (DBA/Trade name)
            </p>
            <div className="create-account-business-name-container">
              <input
                type="text"
                id="businessName"
                name="businessName"
                placeholder="Enter business name (DBA/Trade name)"
                value={formData.businessName}
                onChange={handleInputChange}
                className="create-account-business-name-input"
                required
              />
              <div className="create-account-google-logo">
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="create-account-address-section">
            <div className="create-account-form-row">
              <div className="create-account-form-field">
                <label
                  htmlFor="streetAddress"
                  className="create-account-field-label"
                >
                  Street Address
                </label>
                <input
                  type="text"
                  id="streetAddress"
                  name="streetAddress"
                  placeholder="Enter Your Street Address"
                  value={formData.streetAddress}
                  onChange={handleInputChange}
                  className="create-account-customer-input"
                  required
                />
              </div>

              <div className="create-account-form-field">
                <label
                  htmlFor="suiteUnitFloor"
                  className="create-account-field-label"
                >
                  Suite/Unit/Floor
                </label>
                <input
                  type="text"
                  id="suiteUnitFloor"
                  name="suiteUnitFloor"
                  placeholder="Enter your suite, unit, etc"
                  value={formData.suiteUnitFloor}
                  onChange={handleInputChange}
                  className="create-account-customer-input"
                />
              </div>
            </div>

            <div className="create-account-form-row">
              <div className="create-account-form-field">
                <label htmlFor="city" className="create-account-field-label">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  placeholder="Enter Your City"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="create-account-customer-input"
                  required
                />
              </div>

              <div className="create-account-form-field">
                <label htmlFor="state" className="create-account-field-label">
                  State
                </label>
                <div className="create-account-state-selector">
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="create-account-customer-input create-account-state-select"
                    required
                  >
                    <option value="">Select Your State/Province</option>
                    {usStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="create-account-form-row">
              <div className="create-account-form-field">
                <label htmlFor="zipCode" className="create-account-field-label">
                  ZIP Code
                </label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  placeholder="Enter your ZIP Code"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  className="create-account-customer-input"
                  required
                />
              </div>

              <div className="create-account-form-field">
                <label
                  htmlFor="businessCountry"
                  className="create-account-field-label"
                >
                  Country
                </label>
                <input
                  type="text"
                  id="businessCountry"
                  name="businessCountry"
                  value={formData.businessCountry}
                  onChange={handleInputChange}
                  className="create-account-customer-input"
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="create-account-form-field">
            <label htmlFor="storeNumber" className="create-account-field-label">
              Store Number
            </label>
            <div className="create-account-store-container">
              <div className="create-account-store-country-code-selector">
                <div className="create-account-store-country-code-display">
                  <img
                    src={
                      countryCodes.find(
                        (item) => item.code === formData.storeCountryCode,
                      )?.flag || countryCodes[0].flag
                    }
                    alt="flag"
                    className="create-account-store-flag-image"
                  />
                  <span className="create-account-store-country-code-text">
                    {formData.storeCountryCode}
                  </span>
                  <span className="create-account-store-dropdown-arrow">▼</span>
                </div>
                <select
                  name="storeCountryCode"
                  value={formData.storeCountryCode}
                  onChange={handleInputChange}
                  className="create-account-store-country-code-select"
                >
                  {countryCodes.map((item) => (
                    <option
                      key={`${item.code}-${item.country}`}
                      value={item.code}
                    >
                      {item.display}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="tel"
                id="storeNumber"
                name="storeNumber"
                placeholder="123-456-789"
                value={formData.storeNumber}
                onChange={handleInputChange}
                className="create-account-store-input"
                required
              />
            </div>
          </div>

          <div className="create-account-form-field">
            <label
              htmlFor="companyWebsite"
              className="create-account-field-label"
            >
              Company Website
            </label>
            <input
              type="url"
              id="companyWebsite"
              name="companyWebsite"
              placeholder="Enter your company website, or website, or social media page"
              value={formData.companyWebsite}
              onChange={handleInputChange}
              className="create-account-customer-input"
            />
          </div>

          <div className="create-account-form-field">
            <label htmlFor="taxIdNumber" className="create-account-field-label">
              EIN / Federal Tax ID
            </label>
            <div className="create-account-tax-id-container">
              <div className="create-account-tax-id-type-selector">
                <select
                  name="taxIdType"
                  value={formData.taxIdType}
                  onChange={handleInputChange}
                  className="create-account-customer-input create-account-tax-id-type-select"
                >
                  {taxIdTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                id="taxIdNumber"
                name="taxIdNumber"
                placeholder="123-456-789"
                value={formData.taxIdNumber}
                onChange={handleInputChange}
                className="create-account-tax-id-input"
                required
              />
            </div>
            <button type="button" className="create-account-add-tax-link">
              <span className="create-account-plus-icon">+</span>
              Add another form of Sales Tax Registration Number
            </button>
          </div>

          <div className="create-account-separator"></div>

          <div className="create-account-additional-info-section">
            <h3 className="create-account-additional-info-title">
              Additional Information
            </h3>

            <div className="create-account-form-row">
              <div className="create-account-form-field">
                <label
                  htmlFor="preferredLanguage"
                  className="create-account-field-label"
                >
                  Preferred Language
                </label>
                <div className="create-account-language-selector">
                  <select
                    id="preferredLanguage"
                    name="preferredLanguage"
                    value={formData.preferredLanguage}
                    onChange={handleInputChange}
                    className="create-account-customer-input create-account-language-select"
                  >
                    {languages.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="create-account-form-field">
                <label
                  htmlFor="preferredCurrency"
                  className="create-account-field-label"
                >
                  Preferred Currency
                </label>
                <div className="create-account-currency-selector">
                  <select
                    id="preferredCurrency"
                    name="preferredCurrency"
                    value={formData.preferredCurrency}
                    onChange={handleInputChange}
                    className="create-account-customer-input create-account-currency-select"
                  >
                    {currencies.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="create-account-form-field">
              <label
                htmlFor="howDidYouHear"
                className="create-account-field-label"
              >
                How did you hear about us?
              </label>
              <div className="create-account-hear-about-selector">
                <select
                  id="howDidYouHear"
                  name="howDidYouHear"
                  value={formData.howDidYouHear}
                  onChange={handleInputChange}
                  className="create-account-customer-input create-account-hear-about-select"
                >
                  <option value="">Select an option</option>
                  {howDidYouHearOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="create-account-form-field">
              <label
                htmlFor="businessDescription"
                className="create-account-field-label"
              >
                What best describes your business?
              </label>
              <div className="create-account-business-description-selector">
                <select
                  id="businessDescription"
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleInputChange}
                  className="create-account-customer-input create-account-business-description-select"
                >
                  <option value="">Select an option</option>
                  {businessDescriptionOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="create-account-checkbox-container">
              <input
                type="checkbox"
                id="newsletterUpdates"
                name="newsletterUpdates"
                checked={formData.newsletterUpdates}
                onChange={handleInputChange}
                className="create-account-checkbox"
              />
              <label
                htmlFor="newsletterUpdates"
                className="create-account-checkbox-label"
              >
                I'd like to receive newsletter updates and marketing emails
                about new products.
              </label>
            </div>
            <div className="create-account-consent-section">
              <div className="create-account-checkbox-container">
                <input
                  type="checkbox"
                  id="termsAndConditions"
                  name="termsAndConditions"
                  checked={formData.termsAndConditions}
                  onChange={handleInputChange}
                  className="create-account-checkbox"
                  required
                />
                <label
                  htmlFor="termsAndConditions"
                  className="create-account-checkbox-label"
                >
                  I acknowledge that Wireox is a B2B business and I agree to the{" "}
                  <button type="button" className="create-account-terms-link">
                    Terms and Conditions
                  </button>{" "}
                  and{" "}
                  <button type="button" className="create-account-privacy-link">
                    Privacy Policy
                  </button>
                  .
                </label>
              </div>
            </div>
            <div className="create-account-submit-section">
              {RECAPTCHA_SITE_KEY ? (
                <p className="create-account-recaptcha create-account-recaptcha-v3">
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>
              ) : null}
              <button
                type="submit"
                className="create-account-submit-btn"
                disabled={
                  !RECAPTCHA_SITE_KEY ||
                  loading ||
                  passwordMatchError !== "" ||
                  (formData.password &&
                    formData.password !== formData.confirmPassword) ||
                  userNameError !== ""
                }
              >
                {loading ? "Creating Account..." : "Create account"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <div
          className="otp-modal-overlay"
          onClick={() => !otpVerifying && setShowOTPModal(false)}
        >
          <div
            className="otp-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="otp-modal-title">ENTER CONFIRMATION CODE</h2>
            <p className="otp-modal-instruction">
              A 6-digit code was sent to {formData.email || "your email"}
            </p>

            {otpError && <div className="otp-error-message">{otpError}</div>}

            {successMessage && (
              <div className="otp-success-message">{successMessage}</div>
            )}

            <div className="otp-input-container">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  type="text"
                  className={`otp-input ${otpError ? "otp-input-error" : ""}`}
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  onPaste={handleOTPPaste}
                  maxLength={1}
                  inputMode="numeric"
                  disabled={otpVerifying}
                />
              ))}
            </div>

            <div className="otp-resend-container">
              <button
                type="button"
                className="otp-resend-link"
                onClick={handleResendCode}
                disabled={otpVerifying}
              >
                {otpVerifying ? "Sending..." : "Resend Code"}
              </button>
            </div>

            <button
              type="button"
              className="otp-continue-btn"
              onClick={handleOTPContinue}
              disabled={otpVerifying}
            >
              {otpVerifying ? "Verifying..." : "Continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function CreateAccountWithV3() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    logRecaptcha("Provider mounted", {
      executeRecaptchaReady: typeof executeRecaptcha === "function",
    });
  }, [executeRecaptcha]);

  return (
    <CreateAccountForm
      getRecaptchaToken={async () => {
        if (!executeRecaptcha) {
          logRecaptcha("getRecaptchaToken: executeRecaptcha not ready yet");
          return null;
        }
        logRecaptcha('Calling executeRecaptcha("create_account")');
        const token = await executeRecaptcha("create_account");
        logRecaptcha("executeRecaptcha resolved", {
          hasToken: Boolean(token),
          length: token?.length ?? 0,
        });
        return token;
      }}
    />
  );
}

function CreateAccount() {
  const key = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

  useEffect(() => {
    if (!key) {
      logRecaptcha(
        "No REACT_APP_RECAPTCHA_SITE_KEY — v3 disabled (set key in .env to enable)",
      );
    } else {
      logRecaptcha("Page load: v3 enabled", {
        keyPrefix: `${key.slice(0, 8)}…`,
      });
    }
  }, [key]);

  if (!key) {
    return <CreateAccountForm getRecaptchaToken={null} />;
  }
  return (
    <GoogleReCaptchaProvider reCaptchaKey={key}>
      <CreateAccountWithV3 />
    </GoogleReCaptchaProvider>
  );
}

export default CreateAccount;
